import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, FolderOpen, Layers, Pencil, Plus, Search, Trash2, Upload } from 'lucide-react'
import type { Series } from '../../types/domain.js'
import type { Program } from '../../types/index.js'
import { seriesApi } from '../../lib/api.js'
import { formatDuration } from '../../lib/utils.js'
import type { ProbeResult } from '../../lib/mediaBrowserApi.js'
import { Checkbox } from '../ui/checkbox.js'
import { Spinner } from '../common/Spinner.js'
import { SeriesModal } from './SeriesModal.js'
import { SeriesBulkImportModal } from './SeriesBulkImportModal.js'
import { MediaBrowserModal } from './MediaBrowserModal.js'
import { ProgramBulkActions } from './ProgramBulkActions.js'
import { NewSeriesEpisodeModal } from './NewSeriesEpisodeModal.js'
import { updateRangeSelection } from '../../lib/range-selection.js'
import { filterEpisodes } from './seriesEpisodes.js'
import {
  episodeAirDate,
  episodeState,
  episodeTotalDurationSec,
  episodesOfSeason,
  formatAirDate,
  nextUpcomingEpisodeId,
  orderEpisodesForDisplay,
  seasonRangeLabel,
  seriesCatalogTotals,
  seriesCoherence,
  seriesPeriodicityLabel,
  seriesSeasons,
  seriesTargetDurationSec,
  type SeriesEpisode,
} from './seriesInsights.js'

/**
 * Écran Séries repris de la maquette Pen `BH4Xv`.
 *
 * Trois colonnes fixes (`269,5 / 1fr / 314,5 px`) : le catalogue des séries, la
 * saison ouverte avec ses épisodes, puis l'inspecteur éditorial. Les poignées
 * de redimensionnement du shell d'édition disparaissent ici — la lecture d'un
 * catalogue a une disposition stable, le réglage fin n'a de sens qu'une fois
 * une fiche programme ouverte.
 */

interface SeriesWorkbenchProps {
  channelId:        string
  series:           Series[]
  loading:          boolean
  selectedSeriesId: string | null
  onSelectSeries:   (series: Series) => void
  onSelectProgram:  (program: Program) => void
  onChanged:        () => void
  canManage:        boolean
  /**
   * Horodatage de référence. Injecté par les stories et les tests pour que
   * « Aujourd'hui » et l'état « PRÊT » ne dépendent pas de l'horloge de la
   * machine ; la vue le laisse vide et lit l'heure courante.
   */
  now?:             number
}

type SeriesSort = 'title-asc' | 'title-desc' | 'episodes-desc' | 'genre'

const SORT_LABELS: Record<SeriesSort, string> = {
  'title-asc':     'Titre (A-Z)',
  'title-desc':    'Titre (Z-A)',
  'episodes-desc': 'Nombre d’épisodes',
  'genre':         'Par genre',
}

/** Teinte stable par série, pour que le repère de la liste ne bouge jamais. */
const MARKER_HUES = ['#1A5FD0', '#7F77DD', '#639922', '#378ADD', '#6C778B', '#D17A22']

function markerColor(series: Series, index: number): string {
  return series.programCategory?.color ?? MARKER_HUES[index % MARKER_HUES.length]
}

export const SeriesWorkbench = memo(function SeriesWorkbench({
  channelId, series, loading, selectedSeriesId, onSelectSeries, onSelectProgram, onChanged, canManage, now: nowProp,
}: SeriesWorkbenchProps) {
  const [search, setSearch]       = useState('')
  const [sort, setSort]           = useState<SeriesSort>('title-asc')
  const [season, setSeason]       = useState<number | null>(null)
  const [editing, setEditing]     = useState<Series | undefined>(undefined)
  const [showModal, setShowModal] = useState(false)
  const [creatingEpisode, setCreatingEpisode] = useState<Series | null>(null)
  const [bulkImporting, setBulkImporting]     = useState<Series | null>(null)
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set())
  const [removing, setRemoving]     = useState(false)
  const [episodeSearch, setEpisodeSearch] = useState('')
  // Miniature de la série, reprise de l'ancienne fiche : prélèvement depuis un
  // média, import d'un fichier, retrait.
  const [thumbMenuOpen, setThumbMenuOpen] = useState(false)
  const [browsingMedia, setBrowsingMedia] = useState(false)
  const [thumbBusy, setThumbBusy]         = useState(false)
  const [thumbError, setThumbError]       = useState('')
  const thumbFileInputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<string | null>(null)

  const filtered = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('fr-FR')
    const list = needle
      ? series.filter(item => item.title.toLocaleLowerCase('fr-FR').includes(needle)
        || (item.programCode ?? '').toLocaleLowerCase('fr-FR').includes(needle))
      : series
    return [...list].sort((a, b) => {
      if (sort === 'title-asc')     return a.title.localeCompare(b.title, 'fr')
      if (sort === 'title-desc')    return b.title.localeCompare(a.title, 'fr')
      if (sort === 'episodes-desc') return (b._count?.programs ?? 0) - (a._count?.programs ?? 0)
      // Le genre publié (liste fermée) ; l'ancien texte libre ne sert que de
      // repli aux séries pas encore reclassées.
      const genreOf = (item: Series) => item.programGenre?.label ?? item.genre ?? ''
      return genreOf(a).localeCompare(genreOf(b), 'fr')
    })
  }, [series, search, sort])

  const totals = useMemo(() => seriesCatalogTotals(series), [series])
  const selected = useMemo(
    () => series.find(item => item.id === selectedSeriesId) ?? null,
    [series, selectedSeriesId],
  )

  const allEpisodes = useMemo<SeriesEpisode[]>(() => selected?.programs ?? [], [selected])
  const seasons     = useMemo(() => seriesSeasons(allEpisodes), [allEpisodes])

  // Ouvrir une autre série remet la saison sur la plus récente : conserver le
  // numéro d'une série à l'autre afficherait une saison inexistante.
  useEffect(() => {
    setSeason(seasons[0] ?? null)
    setCheckedIds(new Set())
    setEpisodeSearch('')
    anchorRef.current = null
  }, [selectedSeriesId, seasons])

  const seasonEpisodes = useMemo(
    () => episodesOfSeason(allEpisodes, season),
    [allEpisodes, season],
  )
  /**
   * Une série peut porter plus de cent épisodes : sans recherche, en retrouver
   * un impose de parcourir toute la saison. `filterEpisodes` est la règle
   * partagée avec l'ancienne liste — elle accepte `S02E04` comme `S2E04` et
   * trie par saison puis par numéro, ce qu'une comparaison de chaîne écrite
   * ici raterait.
   */
  const episodes = useMemo(
    () => orderEpisodesForDisplay(
      filterEpisodes(
        seasonEpisodes as unknown as Program[],
        episodeSearch,
        selected?.numberEpisodes ?? false,
      ) as unknown as SeriesEpisode[],
      selected?.episodeSortDescending ?? true,
    ),
    [seasonEpisodes, episodeSearch, selected],
  )
  // La cible se calcule sur toute la série, pas sur la seule saison ouverte :
  // une saison courte donnerait une médiane instable.
  const targetSec = useMemo(() => seriesTargetDurationSec(allEpisodes), [allEpisodes])
  const coherence = useMemo(
    () => selected ? seriesCoherence(selected, allEpisodes) : null,
    [selected, allEpisodes],
  )

  // Horodatage figé pour ce rendu : l'état « PRÊT » et le libellé
  // « Aujourd'hui » doivent se décider sur la même seconde, sinon un rendu à
  // cheval sur minuit afficherait un épisode à venir daté d'hier.
  const now = useMemo(() => nowProp ?? Date.now(), [nowProp, episodes])
  const upcomingId = useMemo(() => nextUpcomingEpisodeId(episodes, now), [episodes, now])

  const episodeIds = useMemo(() => episodes.map(episode => episode.id), [episodes])

  const handleCheck = useCallback((episodeId: string, checked: boolean, shiftKey: boolean) => {
    setCheckedIds(previous => shiftKey
      ? updateRangeSelection(episodeIds, previous, anchorRef.current, episodeId, checked)
      : (() => {
          const next = new Set(previous)
          if (checked) next.add(episodeId)
          else next.delete(episodeId)
          return next
        })())
    if (!shiftKey) anchorRef.current = episodeId
  }, [episodeIds])

  const allChecked  = episodeIds.length > 0 && episodeIds.every(id => checkedIds.has(id))
  const someChecked = episodeIds.some(id => checkedIds.has(id))

  /**
   * « Tout sélectionner » ne porte que sur ce qui est affiché : cocher en
   * aveugle des épisodes masqués par la recherche ferait agir une action
   * groupée sur des fiches que l'opérateur ne voit pas.
   */
  const handleCheckAll = useCallback(() => {
    setCheckedIds(allChecked ? new Set() : new Set(episodeIds))
    anchorRef.current = null
  }, [allChecked, episodeIds])

  const handleDetach = useCallback(async () => {
    if (!selected || !canManage || removing || checkedIds.size === 0) return
    const count = checkedIds.size
    if (!confirm(`Retirer ${count} épisode${count > 1 ? 's' : ''} de « ${selected.title} » ?\n\nLes programmes seront conservés.`)) return
    setRemoving(true)
    try {
      const results = await Promise.allSettled(
        [...checkedIds].map(id => seriesApi.detachProgram(selected.id, id)),
      )
      const failed = results.filter(result => result.status === 'rejected').length
      setCheckedIds(new Set())
      onChanged()
      if (failed > 0) alert(`${failed} retrait${failed > 1 ? 's' : ''} n’ont pas pu être effectué${failed > 1 ? 's' : ''}.`)
    } finally {
      setRemoving(false)
    }
  }, [selected, canManage, removing, checkedIds, onChanged])

  const handleDelete = useCallback(async (item: Series) => {
    if (!confirm(`Supprimer la série « ${item.title} » ?\n\nLes épisodes ne seront pas supprimés, ils seront juste détachés.`)) return
    try {
      await seriesApi.delete(item.id)
      onChanged()
    } catch (error: unknown) {
      alert(error instanceof Error ? error.message : 'Suppression impossible')
    }
  }, [onChanged])

  const handleThumbFromMedia = useCallback(async (result: ProbeResult) => {
    if (!selected) return
    setBrowsingMedia(false)
    setThumbBusy(true)
    setThumbError('')
    try {
      await seriesApi.thumbnailFromMedia(selected.id, result.filePath)
      onChanged()
    } catch (cause) {
      setThumbError(cause instanceof Error ? cause.message : 'Prélèvement impossible')
    } finally {
      setThumbBusy(false)
    }
  }, [selected, onChanged])

  const handleThumbImport = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !selected) return
    setThumbBusy(true)
    setThumbError('')
    try {
      await seriesApi.importThumbnail(selected.id, file)
      onChanged()
    } catch (cause) {
      setThumbError(cause instanceof Error ? cause.message : 'Import impossible')
    } finally {
      setThumbBusy(false)
    }
  }, [selected, onChanged])

  const handleThumbRemove = useCallback(async () => {
    if (!selected) return
    setThumbMenuOpen(false)
    if (!confirm('Retirer la miniature de cette série ?')) return
    setThumbBusy(true)
    setThumbError('')
    try {
      await seriesApi.clearThumbnail(selected.id)
      onChanged()
    } catch (cause) {
      setThumbError(cause instanceof Error ? cause.message : 'Suppression impossible')
    } finally {
      setThumbBusy(false)
    }
  }, [selected, onChanged])

  return (
    <div className="series-workbench">

      <div className="series-toolbar">
        <div className="series-toolbar__search">
          <Search size={13} aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Rechercher une série…"
            aria-label="Rechercher une série"
          />
        </div>

        <select
          className="series-toolbar__sort"
          value={sort}
          onChange={event => setSort(event.target.value as SeriesSort)}
          aria-label="Trier les séries"
        >
          {(Object.keys(SORT_LABELS) as SeriesSort[]).map(key => (
            <option key={key} value={key}>{SORT_LABELS[key]}</option>
          ))}
        </select>

        <div className="series-toolbar__stats">
          <span><strong>{totals.series}</strong> séries</span>
          <span><strong>{totals.episodes}</strong> épisodes</span>
          <span><strong>{totals.active}</strong> actives</span>
        </div>

        <button
          type="button"
          className="tp-button tp-button--primary series-toolbar__new"
          onClick={() => { setEditing(undefined); setShowModal(true) }}
          disabled={!canManage}
          title={canManage ? undefined : 'Permission de gestion des collections et séries requise'}
        >
          <Plus size={14} aria-hidden="true" /> Nouvelle série
        </button>
      </div>

      <div className="series-columns">

        {/* ── Colonne 1 — catalogue des séries ─────────────────────────── */}
        <section className="series-list" aria-label="Catalogue des séries">
          <header className="series-list__header">
            <span>{filtered.length} SÉRIE{filtered.length > 1 ? 'S' : ''}</span>
          </header>
          <div className="series-list__scroll">
            {loading ? (
              <div className="series-empty"><Spinner /> Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="series-empty">
                <strong>Aucune série</strong>
                <span>Créez-en une pour regrouper vos épisodes.</span>
              </div>
            ) : filtered.map((item, index) => {
              const count = item._count?.programs ?? item.programs?.length ?? 0
              const seasonCount = seriesSeasons(item.programs ?? []).length || item.totalSeasons || 0
              const active = item.id === selectedSeriesId
              return (
                <div
                  key={item.id}
                  className={`series-row${active ? ' is-active' : ''}`}
                  style={{ '--series-accent': markerColor(item, index) } as React.CSSProperties}
                >
                  <button
                    type="button"
                    className="series-row__open"
                    onClick={() => onSelectSeries(item)}
                    aria-current={active ? 'true' : undefined}
                  >
                    <span className="series-row__marker" aria-hidden="true">
                      <Layers size={16} />
                    </span>
                    <span className="series-row__copy">
                      <strong>{item.title}</strong>
                      <small>
                        {count} ép.{seasonCount > 0 ? ` · ${seasonCount} saison${seasonCount > 1 ? 's' : ''}` : ''}
                      </small>
                      <span className={`series-row__state${item.active ? '' : ' is-archived'}`}>
                        {item.active ? 'ACTIF' : 'ARCHIVE'}
                      </span>
                    </span>
                  </button>
                  <div className="series-row__actions">
                    <button
                      type="button"
                      onClick={() => setCreatingEpisode(item)}
                      disabled={!canManage}
                      aria-label={`Ajouter un épisode à ${item.title}`}
                      title="Ajouter un épisode"
                    >
                      <Plus size={13} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setBulkImporting(item)}
                      disabled={!canManage}
                      aria-label={`Importer des médias pour ${item.title}`}
                      title="Importer les médias en bloc"
                    >
                      <Upload size={13} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setEditing(item); setShowModal(true) }}
                      disabled={!canManage}
                      aria-label={`Modifier ${item.title}`}
                      title="Modifier la série"
                    >
                      <Pencil size={13} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => void handleDelete(item)}
                      disabled={!canManage}
                      aria-label={`Supprimer ${item.title}`}
                      title="Supprimer la série"
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* ── Colonne 2 — saison ouverte et ses épisodes ───────────────── */}
        <section className="series-season" aria-label="Saisons et épisodes">
          {!selected ? (
            <div className="series-empty series-empty--center">
              <Layers size={24} aria-hidden="true" />
              <strong>Sélectionnez une série</strong>
              <span>Ses saisons et ses épisodes s’afficheront ici.</span>
            </div>
          ) : (
            <>
              <header className="series-season__header">
                <div>
                  <h2>{selected.title}</h2>
                  <p>
                    {season != null ? `Saison ${season}` : 'Toutes les éditions'}
                    {seasonRangeLabel(episodes) ? ` · ${seasonRangeLabel(episodes)}` : ''}
                  </p>
                </div>
                <div className="series-season__actions">
                  {seasons.length > 0 && (
                    <div className="series-season__picker">
                      <select
                        value={season ?? ''}
                        onChange={event => setSeason(event.target.value === '' ? null : Number(event.target.value))}
                        aria-label="Choisir la saison"
                      >
                        <option value="">Toutes les saisons</option>
                        {seasons.map(value => <option key={value} value={value}>Saison {value}</option>)}
                      </select>
                      <ChevronDown size={13} aria-hidden="true" />
                    </div>
                  )}
                  <button
                    type="button"
                    className="tp-button tp-button--primary"
                    onClick={() => setCreatingEpisode(selected)}
                    disabled={!canManage}
                  >
                    <Plus size={14} aria-hidden="true" /> Ajouter un épisode
                  </button>
                </div>
              </header>

              {/* Recherche et actions groupées : mêmes gestes qu'au catalogue —
                  case d'en-tête, Maj+clic pour une plage, plan avant écriture. */}
              <div className="series-episodes__toolbar">
                <div className="series-toolbar__search">
                  <Search size={13} aria-hidden="true" />
                  <input
                    type="search"
                    value={episodeSearch}
                    onChange={event => setEpisodeSearch(event.target.value)}
                    placeholder="Rechercher un épisode, un numéro…"
                    aria-label="Rechercher un épisode"
                  />
                </div>
                <span className="series-episodes__tally" role="status">
                  {checkedIds.size > 0
                    ? `${checkedIds.size} sélectionné${checkedIds.size > 1 ? 's' : ''} · ${episodes.length} affiché${episodes.length > 1 ? 's' : ''}`
                    : `${episodes.length} épisode${episodes.length > 1 ? 's' : ''}`}
                </span>
                {checkedIds.size > 0 && (
                  <>
                    <ProgramBulkActions
                      channelId={channelId}
                      selectedIds={[...checkedIds]}
                      canDelete={canManage}
                      onDone={() => { setCheckedIds(new Set()); anchorRef.current = null; onChanged() }}
                    />
                    <button
                      type="button"
                      className="tp-button tp-button--danger-outline"
                      onClick={() => void handleDetach()}
                      disabled={!canManage || removing}
                    >
                      {removing ? 'Retrait…' : `Retirer de la série (${checkedIds.size})`}
                    </button>
                  </>
                )}
              </div>

              <div className="series-episodes__head" role="row">
                <Checkbox
                  className="series-episode__check"
                  checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                  disabled={!canManage || episodes.length === 0}
                  onClick={handleCheckAll}
                  aria-label="Sélectionner tous les épisodes affichés"
                />
                <span>N°</span>
                <span>Épisode</span>
                <span>Diffusion</span>
                <span>Durée</span>
                <span>État</span>
              </div>

              <div className="series-episodes" role="table" aria-label="Épisodes de la saison">
                {episodes.length === 0 ? (
                  <div className="series-empty series-empty--center">
                    <strong>Aucun épisode</strong>
                    <span>Ajoutez une édition pour démarrer cette saison.</span>
                  </div>
                ) : episodes.map(episode => {
                  const state = episodeState(episode, targetSec, { upcoming: episode.id === upcomingId })
                  const checked = checkedIds.has(episode.id)
                  const airDate = episodeAirDate(episode)
                  return (
                    <div
                      key={episode.id}
                      className={`series-episode${checked ? ' is-checked' : ''}${episode.id === upcomingId ? ' is-upcoming' : ''}`}
                      role="row"
                    >
                      <Checkbox
                        className="series-episode__check"
                        checked={checked}
                        disabled={!canManage}
                        onClick={event => {
                          event.stopPropagation()
                          handleCheck(episode.id, !checked, event.shiftKey)
                        }}
                        aria-label={`Sélectionner ${episode.title}`}
                      />
                      <button
                        type="button"
                        className="series-episode__open"
                        onClick={() => onSelectProgram(episode as unknown as Program)}
                      >
                        <span className="series-episode__number">
                          {selected.numberEpisodes && episode.episode != null ? episode.episode : '—'}
                        </span>
                        <span className="series-episode__copy">
                          <strong>{episode.title || 'Sans titre'}</strong>
                          <small>{episodeCode(selected, episode)}</small>
                        </span>
                        <span className="series-episode__air">{formatAirDate(airDate, now)}</span>
                        <span className="series-episode__duration">
                          {formatDuration(episodeTotalDurationSec(episode))}
                        </span>
                        <span className={`series-episode__state is-${stateTone(state)}`}>{state}</span>
                      </button>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </section>

        {/* ── Colonne 3 — inspecteur éditorial ─────────────────────────── */}
        <aside className="series-inspector" aria-label="Identité de la série">
          <header className="series-inspector__header">
            <span>IDENTITÉ DE LA SÉRIE</span>
            {selected && (
              <button
                type="button"
                onClick={() => { setEditing(selected); setShowModal(true) }}
                disabled={!canManage}
                aria-label="Modifier la série"
                title="Modifier la série"
              >
                <Pencil size={13} aria-hidden="true" />
              </button>
            )}
          </header>

          {!selected ? (
            <div className="series-empty series-empty--center">
              <span>Aucune série ouverte.</span>
            </div>
          ) : (
            <div className="series-inspector__body">
              <div className="series-inspector__cover-wrap">
                <button
                  type="button"
                  className="series-inspector__cover"
                  style={selected.thumbnailUrl ? { backgroundImage: `url(${selected.thumbnailUrl})` } : undefined}
                  onClick={() => setThumbMenuOpen(open => !open)}
                  disabled={!canManage || thumbBusy}
                  title={canManage ? 'Modifier la miniature' : 'Permission requise'}
                >
                  <strong>{(selected.titleShort || selected.title).toLocaleUpperCase('fr-FR')}</strong>
                  {canManage && (
                    <span className="series-inspector__cover-edit" aria-hidden="true">
                      <Pencil size={12} />
                    </span>
                  )}
                </button>

                {thumbMenuOpen && (
                  <>
                    <button
                      type="button"
                      className="series-inspector__menu-backdrop"
                      aria-label="Fermer"
                      onClick={() => setThumbMenuOpen(false)}
                    />
                    <div className="series-inspector__menu" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setThumbMenuOpen(false); setBrowsingMedia(true) }}
                      >
                        <FolderOpen size={13} aria-hidden="true" /> Choisir dans les médias
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setThumbMenuOpen(false); thumbFileInputRef.current?.click() }}
                      >
                        <Upload size={13} aria-hidden="true" /> Importer une image
                      </button>
                      {selected.thumbnailUrl && (
                        <button
                          type="button"
                          role="menuitem"
                          className="is-danger"
                          onClick={() => { void handleThumbRemove() }}
                        >
                          <Trash2 size={13} aria-hidden="true" /> Retirer la miniature
                        </button>
                      )}
                    </div>
                  </>
                )}

                <input
                  ref={thumbFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={event => { void handleThumbImport(event) }}
                  className="series-inspector__file"
                  aria-hidden="true"
                  tabIndex={-1}
                />

                {thumbError && (
                  <div className="tp-feedback tp-feedback--error">{thumbError}</div>
                )}
              </div>

              {selected.description && (
                <p className="series-inspector__summary">{selected.description}</p>
              )}

              <dl className="series-inspector__meta">
                <div>
                  <dt>Code</dt>
                  <dd>{selected.programCode || '—'}</dd>
                </div>
                <div>
                  <dt>Périodicité</dt>
                  <dd>{seriesPeriodicityLabel(allEpisodes)}</dd>
                </div>
                <div>
                  <dt>Durée cible</dt>
                  <dd>{targetSec == null ? '—' : formatDuration(targetSec)}</dd>
                </div>
                <div>
                  <dt>Saison active</dt>
                  <dd>{season != null ? `Saison ${season}` : selected.numberEpisodes ? '—' : 'Sans saison'}</dd>
                </div>
              </dl>

              {coherence && (
                <section className="series-inspector__coherence">
                  <header>
                    <span>COHÉRENCE</span>
                    <strong>{coherence.score == null ? '—' : `${coherence.score} %`}</strong>
                  </header>
                  <ul>
                    {coherence.checks.map(check => (
                      <li key={check.id}>
                        <span className={`series-dot is-${check.tone}`} aria-hidden="true" />
                        {check.label}
                      </li>
                    ))}
                  </ul>
                  <p className="series-inspector__note">
                    Périodicité, durée cible et cohérence sont déduites des épisodes de la série.
                  </p>
                </section>
              )}
            </div>
          )}
        </aside>
      </div>

      {showModal && canManage && (
        <SeriesModal
          channelId={channelId}
          series={editing}
          onCreated={() => { setShowModal(false); onChanged() }}
          onUpdated={() => { setShowModal(false); setEditing(undefined); onChanged() }}
          onClose={() => { setShowModal(false); setEditing(undefined) }}
        />
      )}
      {bulkImporting && canManage && (
        <SeriesBulkImportModal
          series={bulkImporting}
          onImported={onChanged}
          onClose={() => setBulkImporting(null)}
        />
      )}
      {creatingEpisode && canManage && (
        <NewSeriesEpisodeModal
          series={creatingEpisode}
          onCreated={() => { setCreatingEpisode(null); onChanged() }}
          onClose={() => setCreatingEpisode(null)}
        />
      )}
      {browsingMedia && canManage && (
        <MediaBrowserModal
          onSelect={result => { void handleThumbFromMedia(result) }}
          onClose={() => setBrowsingMedia(false)}
          includeImages
        />
      )}
    </div>
  )
})

/** Code d'édition affiché sous le titre, dans le style `AQ-S05-E128` de Pen. */
function episodeCode(series: Series, episode: SeriesEpisode): string {
  const base = series.programCode || series.titleShort || series.title.slice(0, 3).toLocaleUpperCase('fr-FR')
  if (!series.numberEpisodes || episode.season == null || episode.episode == null) return base
  return `${base}-S${String(episode.season).padStart(2, '0')}-E${String(episode.episode).padStart(2, '0')}`
}

function stateTone(state: ReturnType<typeof episodeState>): 'ok' | 'warning' | 'danger' | 'neutral' {
  if (state === 'PRÊT')       return 'ok'
  if (state === 'COMPLET')    return 'neutral'
  if (state === 'À VÉRIFIER') return 'warning'
  return 'danger'
}
